import { addBreadcrumb } from 'shared/sentry';
import { CallEvent } from 'matrix-js-sdk';
import { CallErrorCode, CallState } from 'matrix-js-sdk/lib/webrtc/call';
import { clearIceRecoveryTimers } from './matrixCallIceUtils';
import { patchCallMetadataRateLimit } from './matrixCallMetadataRateLimit';

const OUTGOING_TIMEOUT_MS = 30 * 1000;
/** Single watchdog before first app-level ICE recovery (aligned with SDK ICE timers, ~30s disconnect hangup). */
const CONNECTING_TIMEOUT_MS = 52 * 1000;
/** Allow TURN refresh + ICE restart + renegotiation time on slow networks. */
const ICE_RESTART_WATCHDOG_MS = 32000;
const FORCE_STOP_MEDIA_DELAY_MS = 5000;

const randomId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `${Date.now()}-${Math.random()}`;
};

export default class CallSession {
  constructor({
    call,
    direction,
    engine,
    onEnd,
    onUpdate,
    optimizeAudio,
    safeHangup,
    safeReject,
    ensureCallEventHandler,
    getFailureCategory,
    callStats,
    attemptIceRecovery,
    onReplaced,
  }) {
    this.call = call;
    this.callId = call?.callId ?? null;
    this.direction = direction;
    this.engine = engine;

    this.onEnd = onEnd;
    this.onUpdate = onUpdate;
    this.optimizeAudio = optimizeAudio;
    this.safeHangup = safeHangup;
    this.safeReject = safeReject;
    this.ensureCallEventHandler = ensureCallEventHandler;
    this.getFailureCategory = getFailureCategory;
    this.callStats = callStats;
    this.attemptIceRecovery = attemptIceRecovery;
    this.onReplacedExternal = onReplaced;

    this.instanceId = randomId();
    this.startedAt = Date.now();
    this.connectedAt = null;
    this.iceRetries = 0;
    this.cleaned = false;
    /** Prevents duplicate m.call.hangup / reject traffic (429 Too Many Requests). */
    this._voipTerminateStarted = false;
    this._boundHandlers = null;
    /** @type {(() => void) | null} */
    this._disposeMetadataRateLimit = null;

    if (this.callId) {
      const entry = this.engine.createCallEntry(this.call, this.direction);
      if (entry) entry.instanceId = this.instanceId;
    }

    this.applyMetadataRateLimit();
    this.attachHandlers();
    this.optimizeAudioSafe();
  }

  applyMetadataRateLimit() {
    try {
      this._disposeMetadataRateLimit?.();
    } catch (_) {}
    this._disposeMetadataRateLimit = null;
    if (!this.call) return;
    try {
      const dispose = patchCallMetadataRateLimit(this.call);
      this._disposeMetadataRateLimit = typeof dispose === 'function' ? dispose : null;
    } catch (_) {
      this._disposeMetadataRateLimit = null;
    }
  }

  isSameCallInstance(call) {
    return Boolean(call?.callId) && Boolean(this.callId) && call.callId === this.callId;
  }

  attachHandlers() {
    const callRef = this.call;
    if (!callRef) return;

    // Bind per-call closures so stale events from an old call instance
    // can't mutate the current session after a replacement/refresh.
    this._boundHandlers = {
      [CallEvent.State]: (newState, oldState) => {
        if (this.cleaned) return;
        if (!this.isSameCallInstance(callRef)) return;
        this.onState(newState, oldState);
      },
      [CallEvent.Hangup]: () => {
        if (this.cleaned) return;
        if (!this.isSameCallInstance(callRef)) return;
        this.onHangup();
      },
      [CallEvent.Error]: (err) => {
        if (this.cleaned) return;
        if (!this.isSameCallInstance(callRef)) return;
        this.onError(err);
      },
      [CallEvent.FeedsChanged]: () => {
        if (this.cleaned) return;
        if (!this.isSameCallInstance(callRef)) return;
        this.onFeedsChanged();
      },
    };

    if (CallEvent.Replaced) {
      this._boundHandlers[CallEvent.Replaced] = (newCall) => {
        if (this.cleaned) return;
        if (!this.isSameCallInstance(callRef)) return;
        this.onReplaced(newCall);
      };
    }

    callRef.on?.(CallEvent.State, this._boundHandlers[CallEvent.State]);
    callRef.on?.(CallEvent.Hangup, this._boundHandlers[CallEvent.Hangup]);
    callRef.on?.(CallEvent.Error, this._boundHandlers[CallEvent.Error]);
    callRef.on?.(
      CallEvent.FeedsChanged,
      this._boundHandlers[CallEvent.FeedsChanged]
    );
    if (CallEvent.Replaced) {
      callRef.on?.(CallEvent.Replaced, this._boundHandlers[CallEvent.Replaced]);
    }
  }

  detachHandlers() {
    try {
      const callRef = this.call;
      const handlers = this._boundHandlers;
      callRef?.off?.(CallEvent.State, handlers?.[CallEvent.State]);
      callRef?.off?.(CallEvent.Hangup, handlers?.[CallEvent.Hangup]);
      callRef?.off?.(CallEvent.Error, handlers?.[CallEvent.Error]);
      callRef?.off?.(CallEvent.FeedsChanged, handlers?.[CallEvent.FeedsChanged]);
      if (CallEvent.Replaced) {
        callRef?.off?.(CallEvent.Replaced, handlers?.[CallEvent.Replaced]);
      }
    } catch (_) {}
    this._boundHandlers = null;
  }

  onState = (newState, oldState) => {
    if (this.cleaned) return;
    if (!this.isSameCallInstance(this.call)) return;
    this.engine.updateState(this.callId, newState);

    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      addBreadcrumb({
        category: 'matrix.call',
        message: 'CallSession state',
        data: {
          callId: this.callId,
          state: newState,
          direction: this.direction,
        },
        level: 'info',
      });
    }

    if (
      newState === CallState.CreateAnswer ||
      newState === CallState.Connecting ||
      newState === CallState.Connected
    ) {
      this.engine.clearTimer(this.callId, 'outgoing');
    }

    if (newState === CallState.Connected) {
      if (!this.connectedAt) this.connectedAt = Date.now();
      this.engine.clearTimers(this.callId);
      this.optimizeAudioSafe();
      this.onUpdate?.({ state: newState, callId: this.callId });
      return;
    }

    if (newState === CallState.Connecting) {
      this.startConnectingWatchdog();
      this.onUpdate?.({ state: newState, callId: this.callId });
      return;
    }

    if (newState === CallState.Ended) {
      this.cleanup({ endedReason: this.call?.hangupReason || CallErrorCode.SignallingFailed });
    }
  };

  onHangup = () => {
    if (this.cleaned) return;
    // Some SDK timing edge-cases can trigger `CallEvent.Hangup` before `call.hangupReason`
    // is fully populated. If we pass `null` here, the CallManager/CallingScreen can fail to
    // redirect (because they wait for a non-null ended reason).
    this.cleanup({ endedReason: this.call?.hangupReason ?? CallErrorCode.SignallingFailed });
  };

  onError = (err) => {
    if (this.cleaned) return;

    const code = err?.code || err?.name || null;
    const shouldAttemptIce = code === CallErrorCode.IceFailed;

    if (shouldAttemptIce) {
      this.attemptIceRecovery?.(this.call, this, {
        maxRetries: 5,
        watchdogMs: ICE_RESTART_WATCHDOG_MS,
        onTerminate: () => this.hangup(CallErrorCode.IceFailed),
      });
      return;
    }

    const message = err?.msg || err?.message || 'Call error';
    this.onUpdate?.({
      error: { code, message },
      state: this.call?.state ?? null,
      callId: this.callId,
    });
  };

  onFeedsChanged = () => {
    if (this.cleaned) return;
    if (!this.isSameCallInstance(this.call)) return;
    if (this.call?.state === CallState.Connected) {
      this.optimizeAudioSafe();
    }
  };

  onReplaced = (newCall) => {
    if (this.cleaned || !newCall?.callId) return;
    const oldCallId = this.callId;
    const oldCall = this.call;
    const oldStartedAt = this.startedAt;
    const oldConnectedAt = this.connectedAt;

    // Stop the old call's media immediately so replaced-chain calls
    // don't continue using stale tracks.
    try {
      this.engine.stopMedia?.(oldCall);
    } catch (_) {}
    try {
      clearIceRecoveryTimers(oldCall);
    } catch (_) {}

    // Drop rate-limit patch on the retiring call before swapping instances.
    try {
      this._disposeMetadataRateLimit?.();
    } catch (_) {}
    this._disposeMetadataRateLimit = null;

    this.detachHandlers();
    this.call = newCall;
    this.callId = newCall.callId;

    const entry = this.engine.createCallEntry(this.call, this.direction);
    if (entry) entry.instanceId = this.instanceId;

    // Transfer timers and metrics to the replacement call object.
    this.engine.clearTimers(oldCallId);
    this.startedAt = oldStartedAt;
    this.connectedAt = oldConnectedAt;
    if (oldConnectedAt) {
      this.engine.updateState(this.callId, CallState.Connected);
    }

    this.applyMetadataRateLimit();
    this.attachHandlers();
    this.optimizeAudioSafe();
    this.onReplacedExternal?.({ session: this, oldCall, oldCallId, newCall });
    this.onUpdate?.({ replaced: true, callId: this.callId });
  };

  async answer() {
    if (this.cleaned || !this.call) return null;

    const state = this.call.state;
    if (state !== CallState.Ringing && state !== CallState.CreateAnswer) {
      // Ignore invalid answer attempts (prevents broken SDP transitions).
      return this.call;
    }

    // Let SDK negotiate tracks from the invite SDP.
    await this.call.answer();
    this.optimizeAudioSafe();
    return this.call;
  }

  async place(type) {
    if (this.cleaned || !this.call) return null;

    if (type === 'video') await this.call.placeVideoCall();
    else await this.call.placeVoiceCall();

    this.startOutgoingTimeout();
    this.optimizeAudioSafe();
    return this.call;
  }

  hangup(reason = CallErrorCode.UserHangup) {
    if (this.cleaned) return;
    if (!this._voipTerminateStarted) {
      this._voipTerminateStarted = true;
      if (this.call && !this.call.callHasEnded?.()) {
        try {
          this.safeHangup?.(this.call, reason, false);
        } catch (_) {}
      }
    }
    this.cleanup({ endedReason: reason });
  }

  reject() {
    if (this.cleaned) return;
    if (!this._voipTerminateStarted) {
      this._voipTerminateStarted = true;
      if (this.call && !this.call.callHasEnded?.()) {
        try {
          this.safeReject?.(this.call);
        } catch (_) {}
      }
    }
    this.cleanup({ endedReason: CallErrorCode.UserHangup });
  }

  isVideoCall() {
    return (
      this.call?.type === 'video' ||
      this.call?.hasLocalVideoTrack === true ||
      this.call?.hasRemoteVideoTrack === true
    );
  }

  startOutgoingTimeout() {
    this.engine.setTimer(
      this.callId,
      'outgoing',
      (call) => {
        if (!call || call.callHasEnded?.()) return;
        if (call.state === CallState.Connected) return;
        this.hangup(CallErrorCode.InviteTimeout);
      },
      OUTGOING_TIMEOUT_MS
    );
  }

  startConnectingWatchdog() {
    this.engine.setTimer(
      this.callId,
      'connecting',
      (call) => {
        if (!call || call.callHasEnded?.()) return;
        if (call.state === CallState.Connected) return;

        if (this.cleaned) return;

        // Give the connection time to form before restarting ICE.
        const timeSinceStart = Date.now() - this.startedAt;
        if (timeSinceStart < 10000) return;

        this.attemptIceRecovery?.(call, this, {
          maxRetries: 5,
          watchdogMs: ICE_RESTART_WATCHDOG_MS,
          onTerminate: () => this.hangup(CallErrorCode.IceFailed),
        });
      },
      CONNECTING_TIMEOUT_MS
    );
  }

  optimizeAudioSafe() {
    try {
      this.optimizeAudio?.(this.call);
    } catch (_) {}
  }

  // Force-stop fallback used for replacement-chain edge cases.
  forceStopMediaFallback(call) {
    if (!call) return;
    setTimeout(() => {
      try {
        if (this.call && !this.call.callHasEnded?.()) return;
        this.engine.stopMedia(call);
      } catch (_) {}
    }, FORCE_STOP_MEDIA_DELAY_MS);
  }

  cleanup({ endedReason = undefined } = {}) {
    if (this.cleaned) return;
    this.cleaned = true;

    const call = this.call;
    const callId = this.callId;

    try {
      this._disposeMetadataRateLimit?.();
    } catch (_) {}
    this._disposeMetadataRateLimit = null;

    this.engine.clearTimers(callId);
    this.detachHandlers();
    this.engine.stopMedia(call);
    try {
      clearIceRecoveryTimers(call);
    } catch (_) {}

    const connected = Boolean(this.connectedAt);
    if (this.callStats) {
      if (connected) {
        this.callStats.connected += 1;
      } else {
        this.callStats.failedToConnect += 1;
        const category = this.getFailureCategory?.(endedReason, call?.hangupParty);
        if (category && this.callStats.failedToConnectByCategory?.[category] != null) {
          this.callStats.failedToConnectByCategory[category] += 1;
        } else if (this.callStats.failedToConnectByCategory?.unknown != null) {
          this.callStats.failedToConnectByCategory.unknown += 1;
        }
      }
    }

    this.onEnd?.(this, {
      endedReason: endedReason ?? call?.hangupReason ?? null,
      hangupParty: call?.hangupParty ?? null,
    });
  }
}
